import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { indexOf } from 'lodash';
import { GridTitle } from './gridTitle';
import { GridRowContainer } from './gridRowContainer';
import { FixedRow } from './FixedRow';
import { GridThatsAll } from './gridThatsAll';

let freezeFirst = false;

/**
 * @todo
 * The quality of this component is poor.
 * Future improvement work should be untertaken to refactor/remove this component
 * and improve the health of the codebase.
 */
class GridTable extends Component {
  constructor(props) {
    super(props);

    this.getAdjustedRowHeight = this.getAdjustedRowHeight.bind(this);
    this.getNodeContent = this.getNodeContent.bind(this);
    this.verifyProps = this.gridScroll.bind(this);
    this.gridScroll = this.gridScroll.bind(this);

    this.state = {
      scrollTop: 0,
      scrollHeight: this.props.bodyHeight,
      clientHeight: this.props.bodyHeight,
    };
  }

  componentDidMount() {
    // After the initial render, see if we need to load additional pages.
    this.gridScroll();
  }

  componentDidUpdate() {
    // After the subsequent renders, see if we need to load additional pages.
    this.gridScroll();
  }

  getAdjustedRowHeight() {
    return this.props.rowHeight + this.props.paddingHeight * 2; // account for padding.
  }

  getNodeContent() {
    this.verifyProps();

    // figure out if we need to wrap the group in one tbody or many
    let anyHasChildren = false;

    // If the data is still being loaded, don't build the nodes unless this is an infinite scroll table.
    if (!this.props.externalIsLoading || this.props.enableInfiniteScroll) {
      let nodeData = this.props.data;
      let aboveSpacerRow = null;
      let belowSpacerRow = null;

      // If we have a row height specified, only render what's going to be visible.
      if (
        this.props.enableInfiniteScroll &&
        this.props.rowHeight !== null &&
        this.refs.scrollable !== undefined
      ) {
        const adjustedHeight = this.getAdjustedRowHeight();
        const visibleRecordCount = Math.ceil(
          this.state.clientHeight / adjustedHeight
        );

        // Inspired by : http://jsfiddle.net/vjeux/KbWJ2/9/
        const displayStart = Math.max(
          0,
          Math.floor(this.state.scrollTop / adjustedHeight) -
            visibleRecordCount * 0.25
        );
        const displayEnd = Math.min(
          displayStart + visibleRecordCount * 1.25,
          this.props.data.length - 1
        );

        // Split the amount of nodes.
        nodeData = nodeData.slice(displayStart, displayEnd + 1);

        // Set the above and below nodes.
        const aboveSpacerRowStyle = {
          height: `${displayStart * adjustedHeight}px`,
        };
        aboveSpacerRow = (
          <tr
            key={`${'above-'} ${aboveSpacerRowStyle.height}`}
            style={aboveSpacerRowStyle}
          />
        );
        let belowSpacerRowStyle = {
          height: `${(this.props.data.length - displayEnd) * adjustedHeight}px`,
        };
        belowSpacerRow = (
          <tr
            key={`${'below-'}${belowSpacerRowStyle.height}`}
            style={belowSpacerRowStyle}
          />
        );
      }

      const nodes = nodeData.map((row, index) => {
        freezeFirst = false;
        const hasChildren =
          typeof row['children'] !== 'undefined' && row['children'].length > 0;
        const uniqueId = this.props.rowSettings.getRowKey(row, index);

        // at least one item in the group has children.
        if (hasChildren) {
          anyHasChildren = hasChildren;
        }
        let showChildren = false;
        if (this.props.showChildrenIds) {
          showChildren = indexOf(this.props.showChildrenIds, row.id) >= 0;
        }

        if (!freezeFirst) {
          return (
            <GridRowContainer
              useGriddleStyles={this.props.useGriddleStyles}
              isSubGriddle={this.props.isSubGriddle}
              parentRowExpandedClassName={this.props.parentRowExpandedClassName}
              parentRowCollapsedClassName={
                this.props.parentRowCollapsedClassName
              }
              parentRowExpandedComponent={this.props.parentRowExpandedComponent}
              parentRowCollapsedComponent={
                this.props.parentRowCollapsedComponent
              }
              data={row}
              key={`${uniqueId} ${'-container'}`}
              uniqueId={uniqueId}
              columnSettings={this.props.columnSettings}
              rowSettings={this.props.rowSettings}
              paddingHeight={this.props.paddingHeight}
              multipleSelectionSettings={this.props.multipleSelectionSettings}
              rowHeight={this.props.rowHeight}
              hasChildren={hasChildren}
              tableClassName={this.props.className}
              onRowClick={this.props.onRowClick}
              rowIndex={index}
              content={this.props.content}
              customClick={this.props.customClick}
              rowClickIndex={this.props.rowClickIndex}
              rowCustomClass={this.props.rowCustomClass}
              showChildren={showChildren}
              customChildRow={this.props.customChildRow}
              customChildRowContainerClass={
                this.props.customChildRowContainerClass
              }
              useCustomChildRow={this.props.useCustomChildRow}
            />
          );
        }
        return false;
      });

      // no data section
      if (this.props.showNoData) {
        const colSpan = this.props.columnSettings.getVisibleColumnCount();
        nodes.push(
          <tr key="no-data-section">
            <td colSpan={colSpan}>{this.props.noDataSection}</td>
          </tr>
        );
      }

      // Add the spacer rows for nodes we're not rendering.
      if (aboveSpacerRow) {
        nodes.unshift(aboveSpacerRow);
      }
      if (belowSpacerRow) {
        nodes.push(belowSpacerRow);
      }

      // Send back the nodes.
      return {
        nodes,
        anyHasChildren,
      };
    } else {
      return null;
    }
  }

  verifyProps() {
    if (this.props.columnSettings === null) {
      console.error(
        "gridTable: The columnSettings prop is null and it shouldn't be"
      );
    }
    if (this.props.rowSettings === null) {
      console.error(
        "gridTable: The rowSettings prop is null and it shouldn't be"
      );
    }
  }

  gridScroll() {
    if (this.props.enableInfiniteScroll && !this.props.externalIsLoading) {
      // If the scroll height is greater than the current amount of rows displayed, update the page.
      const scrollable = this.refs.scrollable;
      const scrollTop = scrollable.scrollTop;
      const scrollHeight = scrollable.scrollHeight;
      const clientHeight = scrollable.clientHeight;

      // If the scroll position changed and the difference is greater than a row height
      if (
        this.props.rowHeight !== null &&
        this.state.scrollTop !== scrollTop &&
        Math.abs(this.state.scrollTop - scrollTop) >=
          this.getAdjustedRowHeight()
      ) {
        const newState = {
          scrollTop,
          scrollHeight,
          clientHeight,
        };

        // Set the state to the new state
        this.setState(newState);
      }
      // Determine the diff by subtracting the amount scrolled by the total height, taking into consideratoin
      // the spacer's height.
      const scrollHeightDiff =
        scrollHeight -
        (scrollTop + clientHeight) -
        this.props.infiniteScrollLoadTreshold;

      // Make sure that we load results a little before reaching the bottom.
      const compareHeight = scrollHeightDiff * 0.6;

      if (compareHeight <= this.props.infiniteScrollLoadTreshold) {
        this.props.nextPage();
      }
    }
  }

  render() {
    let nodes = [];

    // for if we need to wrap the group in one tbody or many todo fix this
    let anyHasChildren = false; // eslint-disable-line

    // Grab the nodes to render
    const nodeContent = this.getNodeContent();
    if (nodeContent) {
      nodes = nodeContent.nodes;
      anyHasChildren = nodeContent.anyHasChildren;
    }

    let gridStyle = null;
    let loadingContent = null;
    const tableStyle = {
      width: '100%',
    };

    if (this.props.useFixedLayout) {
      tableStyle.tableLayout = 'fixed';
    }

    // require for custom pager
    // If we're enabling infinite scrolling, we'll want to include the max height of the grid body + allow scrolling.
    gridStyle = {
      position: 'relative',
      width: '100%',
    };
    if (this.props.minHeigth !== undefined && this.props.minHeigth > 0) {
      gridStyle = {
        position: 'relative',
        width: '100%',
        minHeight: `${this.props.minHeigth}px`,
      };
    }

    // If we're currently loading, populate the loading content
    if (this.props.externalIsLoading) {
      let defaultLoadingStyle = null;
      let defaultColSpan = null;

      if (this.props.useGriddleStyles) {
        defaultLoadingStyle = {
          textAlign: 'center',
          paddingBottom: '40px',
        };
      }

      defaultColSpan = this.props.columnSettings.getVisibleColumnCount();

      const loadingComponent = this.props.externalLoadingComponent ? (
        <this.props.externalLoadingComponent />
      ) : (
        <div>Loading...</div>
      );

      loadingContent = (
        <tbody>
          <tr>
            <td style={defaultLoadingStyle} colSpan={defaultColSpan}>
              {loadingComponent}
            </td>
          </tr>
        </tbody>
      );
    }

    // construct the table heading component
    const tableHeading = this.props.showTableHeading ? (
      <GridTitle
        useGriddleStyles={this.props.useGriddleStyles}
        useGriddleIcons={this.props.useGriddleIcons}
        sortSettings={this.props.sortSettings}
        multipleSelectionSettings={this.props.multipleSelectionSettings}
        columnSettings={this.props.columnSettings}
        filterByColumn={this.props.filterByColumn}
        rowSettings={this.props.rowSettings}
        content={this.props.content}
      />
    ) : undefined;

    let firstNode = undefined;
    if (this.props.freezeFirst) {
      firstNode = (
        <thead>
          <FixedRow
            useGriddleStyles={this.props.useGriddleStyles}
            isSubGriddle={this.props.isSubGriddle}
            parentRowExpandedClassName={this.props.parentRowExpandedClassName}
            parentRowCollapsedClassName={this.props.parentRowCollapsedClassName}
            parentRowExpandedComponent={this.props.parentRowExpandedComponent}
            parentRowCollapsedComponent={this.props.parentRowCollapsedComponent}
            data={this.props.data[0]}
            key={'fixed-container'}
            uniqueId={'uniqueId'}
            columnSettings={this.props.columnSettings}
            rowSettings={this.props.rowSettings}
            paddingHeight={this.props.paddingHeight}
            multipleSelectionSettings={this.props.multipleSelectionSettings}
            rowHeight={this.props.rowHeight}
            hasChildren={false}
            tableClassName={this.props.className}
            onRowClick={this.props.onRowClick}
            rowIndex={0}
            content={this.props.content}
            customClick={this.props.customClick}
            rowClickIndex={this.props.rowClickIndex}
            rowCustomClass={this.props.rowCustomClass}
          />
        </thead>
      );
      nodes = nodes.slice(1);
    }

    let pagingContent = <tbody />;
    if (this.props.showPager) {
      const pagingStyles = this.props.useGriddleStyles
        ? {
            padding: '0px',
            backgroundColor: '#EDEDED',
            border: '0px',
            color: '#222',
            height: this.props.showNoData ? '20px' : null,
          }
        : null;

      pagingContent = (
        <tbody>
          <tr>
            <td
              colSpan={
                this.props.multipleSelectionSettings.isMultipleSelection
                  ? this.props.columnSettings.getVisibleColumnCount() + 1
                  : this.props.columnSettings.getVisibleColumnCount()
              }
              style={pagingStyles}
              className="footer-container"
            >
              {!this.props.showNoData ? this.props.pagingContent : null}
            </td>
          </tr>
        </tbody>
      );
    }

    // If we have a fixed header, split into two tables.
    if (this.props.useFixedHeader) {
      if (this.props.useGriddleStyles) {
        tableStyle.tableLayout = 'fixed';
      }

      return (
        <div>
          <div className="transaction-data-head">
            <div className="transaction-data-head__container">
              <table
                role="grid"
                className={this.props.className}
                style={(this.props.useGriddleStyles && tableStyle) || null}
              >
                {tableHeading}
                {firstNode !== undefined ? firstNode : null}
              </table>
            </div>
          </div>
          <div
            ref="scrollable"
            onScroll={this.gridScroll}
            className="transaction-data-grid"
            style={gridStyle}
          >
            <div className="transaction-data-grid-container">
              <table
                role="grid"
                className={this.props.className}
                style={(this.props.useGriddleStyles && tableStyle) || null}
              >
                {nodes}
                {loadingContent}
              </table>
              {this.props.isFooterEnabled && (
                <GridThatsAll
                  thatsAllMessage={this.props.thatsAllMessage}
                  onFooterClick={this.props.onFooterClick}
                  gridFooterCss={this.props.gridFooterCss}
                  loadTransactionsFlag={this.props.loadTransactionsFlag}
                />
              )}
              <table role="grid">{pagingContent}</table>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div ref="scrollable" onScroll={this.gridScroll} style={gridStyle}>
        <table
          role="grid"
          summary="All transaction Data in Table format"
          className={this.props.className}
          style={(this.props.useGriddleStyles && tableStyle) || null}
        >
          {tableHeading}
          {nodes}
          {loadingContent}
        </table>
        <table>{pagingContent}</table>
      </div>
    );
  }
}

GridTable.defaultProps = {
  data: [],
  columnSettings: null,
  rowSettings: null,
  sortSettings: null,
  multipleSelectionSettings: null,
  className: '',
  enableInfiniteScroll: false,
  nextPage: null,
  hasMorePages: false,
  useFixedHeader: false,
  useFixedLayout: true,
  paddingHeight: null,
  rowHeight: null,
  filterByColumn: null,
  infiniteScrollLoadTreshold: null,
  bodyHeight: null,
  useGriddleStyles: true,
  useGriddleIcons: true,
  isSubGriddle: false,
  parentRowCollapsedClassName: 'parent-row',
  parentRowExpandedClassName: 'parent-row expanded',
  parentRowCollapsedComponent: '▶',
  parentRowExpandedComponent: '▼',
  externalLoadingComponent: null,
  externalIsLoading: false,
  onRowClick: null,
  rowClickIndex: -1,
  rowCustomClass: null,
  thatsAllMessage: "That's all we can find. Try changing your date range?",
  gridFooterCss: 'accountTransactionGridBottomText',
  customChildRow: null,
  customChildRowContainerClass: null,
  useCustomChildRow: null,
};

GridTable.propTypes = {
  content: PropTypes.object,
  data: PropTypes.array.isRequired,
  minHeigth: PropTypes.number,
  bodyHeight: PropTypes.number,
  rowHeight: PropTypes.number,
  paddingHeight: PropTypes.number,
  useFixedHeader: PropTypes.bool,
  freezeFirst: PropTypes.bool,
  externalIsLoading: PropTypes.bool,
  useGriddleIcons: PropTypes.bool,
  enableInfiniteScroll: PropTypes.bool,
  useGriddleStyles: PropTypes.bool,
  useFixedLayout: PropTypes.bool,
  showNoData: PropTypes.bool,
  columnSettings: PropTypes.object,
  rowSettings: PropTypes.object,
  showPager: PropTypes.bool,
  nextPage: PropTypes.func,
  pagingContent: PropTypes.object,
  multipleSelectionSettings: PropTypes.object,
  rowCustomClass: PropTypes.func,
  className: PropTypes.string,
  showTableHeading: PropTypes.bool,
  infiniteScrollLoadTreshold: PropTypes.number,
  externalLoadingComponent: PropTypes.string,
  noDataSection: PropTypes.object,
  sortSettings: PropTypes.object,
  filterByColumn: PropTypes.func,
  onFooterClick: PropTypes.func,
  showChildrenIds: PropTypes.arrayOf(PropTypes.string),
  customChildRow: PropTypes.object,
  customChildRowContainerClass: PropTypes.string,
  useCustomChildRow: PropTypes.func,
};

export { GridTable };
